import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSync,
  faClock,
  faSpinner,
  faCheckCircle,
  faTimes,
  faComment,
  faDatabase,
  faPen,
  faMicrophone,
  faStop,
  faInbox,
  faEye,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import type {
  FeedbackAdminItem,
  FeedbackAdminStatus,
  FeedbackAdminStatsResponse,
  CorrectionType,
  RagCorrectionAction,
  PromptCorrectionAction,
} from '../types';
import { apiService } from '../services/api';
import { RagChunkList } from '../components/RagChunkCard';

// ============================================================================
// Constants
// ============================================================================
const DARK_COLOR = '#444444';
const ACCENT_COLOR = '#fec35c';
const SUCCESS_COLOR = '#22c55e';
const WARNING_COLOR = '#f59e0b';
const ERROR_COLOR = '#ef4444';
const INFO_COLOR = '#3b82f6';
const PURPLE_COLOR = '#8b5cf6';
const CYAN_COLOR = '#0891b2';

// Tabs for filtering - status-based only (not correction type)
type TabId = 'all' | 'pending' | 'analyzed' | 'revising' | 'resolved';

// Default stats when no data is loaded
const DEFAULT_STATS: FeedbackAdminStatsResponse = {
  total: 0,
  pending: 0,
  analyzed: 0,
  analyzing: 0,
  revising: 0,
  approved: 0,
  applying: 0,
  reviewed: 0,
  resolved: 0,
  dismissed: 0,
  validation_failed: 0,
  apply_failed: 0,
  by_correction_type: {
    rag: 0,
    prompt: 0,
    null: 0,
  },
};

// ============================================================================
// Helper Functions
// ============================================================================
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getStatusConfig = (status: FeedbackAdminStatus) => {
  switch (status) {
    case 'pending':
      return { color: WARNING_COLOR, bgColor: '#fef3c7', icon: faClock, label: 'Pending' };
    case 'generating':
    case 'analyzing':
      return { color: INFO_COLOR, bgColor: '#dbeafe', icon: faSpinner, label: 'Generating' };
    case 'revising':
      return { color: CYAN_COLOR, bgColor: '#cffafe', icon: faSync, label: 'Revising' };
    case 'analyzed':
    case 'reviewed':
      return { color: INFO_COLOR, bgColor: '#dbeafe', icon: faEye, label: 'Ready for Review' };
    case 'approved':
      return { color: SUCCESS_COLOR, bgColor: '#dcfce7', icon: faCheckCircle, label: 'Approved' };
    case 'applying':
      return { color: CYAN_COLOR, bgColor: '#cffafe', icon: faSpinner, label: 'Applying' };
    case 'resolved':
      return { color: SUCCESS_COLOR, bgColor: '#dcfce7', icon: faCheckCircle, label: 'Resolved' };
    case 'dismissed':
      return { color: '#6b7280', bgColor: '#f3f4f6', icon: faTimes, label: 'Dismissed' };
    case 'gen_failed':
      return { color: ERROR_COLOR, bgColor: '#fef2f2', icon: faTimes, label: 'Generation Failed' };
    case 'validation_failed':
      return { color: ERROR_COLOR, bgColor: '#fef2f2', icon: faTimes, label: 'Validation Failed' };
    case 'apply_failed':
      return { color: ERROR_COLOR, bgColor: '#fef2f2', icon: faTimes, label: 'Apply Failed' };
    default:
      return { color: DARK_COLOR, bgColor: '#f3f4f6', icon: faClock, label: status };
  }
};

const getCorrectionTypeConfig = (type: CorrectionType) => {
  if (type === 'rag') {
    return { color: PURPLE_COLOR, bgColor: '#f3e8ff', icon: faDatabase, label: 'RAG' };
  }
  if (type === 'prompt') {
    return { color: SUCCESS_COLOR, bgColor: '#dcfce7', icon: faPen, label: 'Prompt' };
  }
  return null;
};

// ============================================================================
// Sub-components
// ============================================================================
const StatusBadge: React.FC<{ status: FeedbackAdminStatus }> = ({ status }) => {
  const config = getStatusConfig(status);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.25rem 0.5rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 500,
        backgroundColor: config.bgColor,
        color: config.color,
      }}
    >
      <FontAwesomeIcon
        icon={config.icon}
        style={{ width: 10, height: 10 }}
        spin={status === 'analyzing' || status === 'revising'}
      />
      {config.label}
    </span>
  );
};

const CorrectionTypeBadge: React.FC<{ type: CorrectionType }> = ({ type }) => {
  const config = getCorrectionTypeConfig(type);
  if (!config) return null;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.25rem 0.5rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 500,
        backgroundColor: config.bgColor,
        color: config.color,
      }}
    >
      <FontAwesomeIcon icon={config.icon} style={{ width: 10, height: 10 }} />
      {config.label}
    </span>
  );
};

const StatCard: React.FC<{
  label: string;
  value: number;
  icon: string;
  bgColor: string;
  textColor: string;
}> = ({ label, value, icon, bgColor, textColor }) => (
  <div
    style={{
      background: 'white',
      padding: '1rem',
      borderRadius: '4px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      border: '1px solid #f3f4f6',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>{label}</p>
        <p style={{ fontSize: '1.25rem', fontWeight: 700, color: textColor }}>{value}</p>
      </div>
      <div
        style={{
          width: '2.5rem',
          height: '2.5rem',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1rem',
          backgroundColor: bgColor,
        }}
      >
        {icon}
      </div>
    </div>
  </div>
);

const FeedbackListItem: React.FC<{
  feedback: FeedbackAdminItem;
  isSelected: boolean;
  onClick: () => void;
}> = ({ feedback, isSelected, onClick }) => {
  // Truncate content for preview
  const preview = feedback.content.length > 100
    ? feedback.content.slice(0, 100) + '...'
    : feedback.content;

  return (
    <div
      onClick={onClick}
      style={{
        padding: '1rem',
        cursor: 'pointer',
        transition: 'all 0.2s',
        borderBottom: '1px solid #f3f4f6',
        backgroundColor: isSelected ? '#fff7ed' : 'white',
        borderLeft: isSelected ? '4px solid #f97316' : '4px solid transparent',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = '#f9fafb';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'white';
        }
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <StatusBadge status={feedback.status} />
          <CorrectionTypeBadge type={feedback.correction_type} />
        </div>
        <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{formatDate(feedback.created_at)}</span>
      </div>
      <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1f2937', marginBottom: '0.25rem' }}>
        {feedback.feedback_type === 'factual_error' ? 'Factual Error' : 'Behavior Issue'}
      </p>
      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem', lineHeight: 1.4 }}>
        {preview}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', color: '#9ca3af' }}>
        <span>👤 {feedback.user_email}</span>
      </div>
    </div>
  );
};

const ConversationBubble: React.FC<{
  content: string;
  isUser: boolean;
  source?: string;
}> = ({ content, isUser, source }) => (
  <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: '0.75rem' }}>
    <div
      style={{
        maxWidth: '80%',
        padding: '0.5rem 1rem',
        borderRadius: '4px',
        fontSize: '0.875rem',
        backgroundColor: isUser ? INFO_COLOR : '#f3f4f6',
        color: isUser ? 'white' : '#1f2937',
        borderTopRightRadius: isUser ? '0.25rem' : '1rem',
        borderTopLeftRadius: isUser ? '1rem' : '0.25rem',
      }}
    >
      <p style={{ margin: 0 }}>{content}</p>
      {source && (
        <p style={{ fontSize: '0.75rem', color: isUser ? 'rgba(255,255,255,0.7)' : '#6b7280', marginTop: '0.5rem' }}>
          📄 Source: {source}
        </p>
      )}
    </div>
  </div>
);

const RagCorrectionPanel: React.FC<{
  action: RagCorrectionAction;
  oldChunk?: { id?: string; source?: string; content?: string };
  newChunk?: { id?: string; source?: string; content?: string };
  suggestedDocument?: string;
}> = ({ action, oldChunk, newChunk, suggestedDocument }) => {
  const getActionConfig = () => {
    switch (action) {
      case 'add':
        return { label: 'Add Chunk', borderColor: '#86efac', headerBg: '#f0fdf4', headerBorder: '#bbf7d0', textColor: '#15803d', icon: '➕' };
      case 'disable':
        return { label: 'Disable Chunk', borderColor: '#fecaca', headerBg: '#fef2f2', headerBorder: '#fecaca', textColor: '#b91c1c', icon: '🚫' };
      case 'replace':
      default:
        return { label: 'Replace Chunk', borderColor: '#d8b4fe', headerBg: '#faf5ff', headerBorder: '#e9d5ff', textColor: '#7c3aed', icon: '🔄' };
    }
  };

  const config = getActionConfig();

  return (
    <div style={{ border: `2px solid ${config.borderColor}`, borderRadius: '4px', overflow: 'hidden' }}>
      <div style={{
        padding: '0.75rem',
        borderBottom: `1px solid ${config.headerBorder}`,
        backgroundColor: config.headerBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ fontWeight: 600, color: config.textColor }}>🗃️ Proposed RAG Correction</span>
        <span style={{
          padding: '0.25rem 0.5rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: action === 'add' ? '#dcfce7' : action === 'disable' ? '#fee2e2' : '#f3e8ff',
          color: config.textColor,
        }}>
          {config.icon} {config.label}
        </span>
      </div>
      <div style={{ padding: '1rem' }}>
        {action === 'replace' && oldChunk && (
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, marginBottom: '0.5rem', textTransform: 'uppercase' }}>
              Current Chunk (to be disabled)
            </p>
            <div style={{ padding: '0.75rem', borderRadius: '4px', backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#6b7280' }}>
                  chunk_id: {oldChunk.id || 'N/A'}
                </span>
                <span style={{ fontSize: '0.75rem', color: '#dc2626' }}>{oldChunk.source}</span>
              </div>
              <p style={{
                backgroundColor: '#fee2e2',
                color: '#991b1b',
                textDecoration: 'line-through',
                padding: '0.5rem',
                borderRadius: '4px',
                fontSize: '0.875rem',
                margin: 0,
              }}>
                {oldChunk.content}
              </p>
            </div>
          </div>
        )}

        {action === 'disable' && oldChunk && (
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, marginBottom: '0.5rem', textTransform: 'uppercase' }}>
              Chunk to Disable (No Replacement)
            </p>
            <div style={{ padding: '0.75rem', borderRadius: '4px', backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#6b7280' }}>
                  chunk_id: {oldChunk.id || 'N/A'}
                </span>
                <span style={{ fontSize: '0.75rem', color: '#dc2626' }}>{oldChunk.source}</span>
              </div>
              <p style={{
                backgroundColor: '#fee2e2',
                color: '#991b1b',
                textDecoration: 'line-through',
                padding: '0.5rem',
                borderRadius: '4px',
                fontSize: '0.875rem',
                margin: 0,
              }}>
                {oldChunk.content}
              </p>
            </div>
            <div style={{ padding: '0.75rem', borderRadius: '4px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', marginTop: '1rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#b91c1c', margin: 0 }}>
                ⚠️ This content will be disabled. No replacement exists - the policy was removed entirely.
              </p>
            </div>
          </div>
        )}

        {(action === 'replace' || action === 'add') && newChunk && (
          <div>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, marginBottom: '0.5rem', textTransform: 'uppercase' }}>
              {action === 'add' ? 'New Content to Add' : 'Replacement Chunk'}
            </p>
            <div style={{
              padding: '0.75rem',
              borderRadius: '4px',
              backgroundColor: '#f0fdf4',
              border: action === 'add' ? '2px dashed #86efac' : '1px solid #bbf7d0',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#6b7280' }}>
                  {action === 'add' && suggestedDocument ? 'Requires document ingest' : `chunk_id: ${newChunk.id || 'N/A'}`}
                </span>
                <span style={{ fontSize: '0.75rem', color: SUCCESS_COLOR }}>{suggestedDocument || newChunk.source}</span>
              </div>
              <p style={{
                backgroundColor: '#dcfce7',
                color: '#166534',
                padding: '0.5rem',
                borderRadius: '4px',
                fontSize: '0.875rem',
                margin: 0,
              }}>
                {newChunk.content}
              </p>
            </div>
            {action === 'add' && (
              <div style={{ padding: '0.75rem', borderRadius: '4px', backgroundColor: '#fefce8', border: '1px solid #fde68a', marginTop: '1rem' }}>
                <p style={{ fontSize: '0.875rem', color: '#854d0e', margin: 0 }}>
                  ℹ️ This will add new knowledge. Click "Approve" to add to the ingest queue.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const PromptCorrectionPanel: React.FC<{
  action: PromptCorrectionAction;
  currentPrompt?: string;
  proposedPrompt?: string;
}> = ({ action, currentPrompt, proposedPrompt }) => {
  const getActionConfig = () => {
    switch (action) {
      case 'add':
        return { label: 'Add Section', borderColor: '#86efac', headerBg: '#f0fdf4', headerBorder: '#bbf7d0', textColor: '#15803d', icon: '➕' };
      case 'remove':
        return { label: 'Remove Section', borderColor: '#fecaca', headerBg: '#fef2f2', headerBorder: '#fecaca', textColor: '#b91c1c', icon: '🚫' };
      case 'replace':
      default:
        return { label: 'Replace Section', borderColor: '#86efac', headerBg: '#f0fdf4', headerBorder: '#bbf7d0', textColor: '#15803d', icon: '🔄' };
    }
  };

  const config = getActionConfig();

  return (
    <div style={{ border: `2px solid ${config.borderColor}`, borderRadius: '4px', overflow: 'hidden' }}>
      <div style={{
        padding: '0.75rem',
        borderBottom: `1px solid ${config.headerBorder}`,
        backgroundColor: config.headerBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ fontWeight: 600, color: config.textColor }}>✏️ Proposed Prompt Update</span>
        <span style={{
          padding: '0.25rem 0.5rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: action === 'add' || action === 'replace' ? '#dcfce7' : '#fee2e2',
          color: config.textColor,
        }}>
          {config.icon} {config.label}
        </span>
      </div>
      <div style={{ padding: '1rem' }}>
        {(action === 'replace' || action === 'remove') && currentPrompt && (
          <div style={{ marginBottom: action === 'remove' ? 0 : '1rem' }}>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, marginBottom: '0.5rem', textTransform: 'uppercase' }}>
              {action === 'remove' ? 'Prompt Section to Remove' : 'Current Prompt Section'}
            </p>
            <div style={{ padding: '0.75rem', borderRadius: '4px', backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
              <p style={{
                backgroundColor: '#fee2e2',
                color: '#991b1b',
                textDecoration: action === 'remove' ? 'line-through' : 'none',
                padding: '0.5rem',
                borderRadius: '4px',
                fontSize: '0.875rem',
                margin: 0,
              }}>
                {currentPrompt}
              </p>
            </div>
            {action === 'remove' && (
              <div style={{ padding: '0.75rem', borderRadius: '4px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', marginTop: '1rem' }}>
                <p style={{ fontSize: '0.875rem', color: '#b91c1c', margin: 0 }}>
                  ⚠️ This instruction will be removed from the system prompt entirely.
                </p>
              </div>
            )}
          </div>
        )}

        {(action === 'replace' || action === 'add') && proposedPrompt && (
          <div>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, marginBottom: '0.5rem', textTransform: 'uppercase' }}>
              {action === 'add' ? 'New Prompt Section to Add' : 'Proposed Update'}
            </p>
            <div style={{
              padding: '0.75rem',
              borderRadius: '4px',
              backgroundColor: '#f0fdf4',
              border: action === 'add' ? '2px dashed #86efac' : '1px solid #bbf7d0',
            }}>
              <p style={{
                backgroundColor: '#dcfce7',
                color: '#166534',
                padding: '0.5rem',
                borderRadius: '4px',
                fontSize: '0.875rem',
                margin: 0,
              }}>
                {proposedPrompt}
              </p>
            </div>
            {action === 'add' && (
              <div style={{ padding: '0.75rem', borderRadius: '4px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', marginTop: '1rem' }}>
                <p style={{ fontSize: '0.875rem', color: '#1e40af', margin: 0 }}>
                  ℹ️ This will add new instructions to the system prompt.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ValidationFailedBanner: React.FC<{
  errors: string[];
  retryCount: number;
  onRetry: () => void;
}> = ({ errors, retryCount, onRetry }) => (
  <div style={{
    padding: '1rem',
    borderRadius: '4px',
    backgroundColor: '#fef2f2',
    border: '2px solid #fecaca',
    marginBottom: '1rem',
  }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
      <div style={{
        width: '2rem',
        height: '2rem',
        borderRadius: '50%',
        backgroundColor: '#fee2e2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <FontAwesomeIcon icon={faTimes} style={{ color: ERROR_COLOR }} />
      </div>
      <div style={{ flex: 1 }}>
        <h4 style={{ margin: '0 0 0.5rem 0', color: '#b91c1c', fontSize: '0.875rem', fontWeight: 600 }}>
          AI Validation Failed ({retryCount} attempts)
        </h4>
        <p style={{ margin: '0 0 0.75rem 0', color: '#7f1d1d', fontSize: '0.875rem' }}>
          The AI was unable to generate a valid correction after {retryCount} attempts.
          The proposed correction contains errors that need manual review.
        </p>
        <div style={{
          backgroundColor: '#fee2e2',
          borderRadius: '4px',
          padding: '0.75rem',
          marginBottom: '0.75rem',
        }}>
          <p style={{ margin: '0 0 0.5rem 0', color: '#991b1b', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>
            Validation Errors:
          </p>
          <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#7f1d1d', fontSize: '0.875rem' }}>
            {errors.map((error, idx) => (
              <li key={idx} style={{ marginBottom: '0.25rem' }}>{error}</li>
            ))}
          </ul>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={onRetry}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              border: 'none',
              background: ERROR_COLOR,
              color: 'white',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <FontAwesomeIcon icon={faRefresh} />
            Retry AI Generation
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Helper function to determine available actions based on feedback status
const getAvailableActions = (status: FeedbackAdminStatus) => {
  switch (status) {
    // In-progress states - no actions
    case 'pending':
    case 'generating':
    case 'analyzing':
    case 'revising':
    case 'applying':
      return { canRevise: false, canApprove: false, canDismiss: false, canRetry: false };

    // Terminal states - no actions
    case 'resolved':
    case 'dismissed':
      return { canRevise: false, canApprove: false, canDismiss: false, canRetry: false };

    // Failed states - can retry or dismiss
    case 'gen_failed':
    case 'validation_failed':
      return { canRevise: false, canApprove: false, canDismiss: true, canRetry: true };

    // Apply failed - can retry apply or dismiss
    case 'apply_failed':
      return { canRevise: false, canApprove: false, canDismiss: true, canRetry: true };

    // Ready for review - full actions (analyzed/reviewed are equivalent)
    case 'analyzed':
    case 'reviewed':
      return { canRevise: true, canApprove: true, canDismiss: true, canRetry: false };

    // Approved but not yet applying - could potentially cancel
    case 'approved':
      return { canRevise: false, canApprove: false, canDismiss: true, canRetry: false };

    default:
      return { canRevise: false, canApprove: false, canDismiss: false, canRetry: false };
  }
};

// Helper to get status explanation for empty action states
const getStatusExplanation = (status: FeedbackAdminStatus): string | null => {
  switch (status) {
    case 'pending':
      return 'Waiting for AI to generate correction...';
    case 'generating':
    case 'analyzing':
      return 'AI is generating a correction...';
    case 'revising':
      return 'AI is revising the correction based on your feedback...';
    case 'applying':
      return 'Correction is being applied...';
    case 'resolved':
      return 'This feedback has been resolved.';
    case 'dismissed':
      return 'This feedback has been dismissed.';
    default:
      return null;
  }
};

const FeedbackDetailPanel: React.FC<{
  feedback: FeedbackAdminItem;
  onRequestRevision: () => void;
  onDismiss: () => void;
  onApprove: () => void;
  onRetryGeneration?: () => void;
}> = ({ feedback, onRequestRevision, onDismiss, onApprove, onRetryGeneration }) => {
  const isRevising = feedback.status === 'revising';
  const isValidationFailed = feedback.status === 'validation_failed';
  const actions = getAvailableActions(feedback.status);
  const statusExplanation = getStatusExplanation(feedback.status);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1f2937', margin: 0 }}>
              {feedback.feedback_type === 'factual_error' ? 'Factual Error' : 'Behavior Issue'}
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>
              Feedback ID: {feedback.id} • {formatDate(feedback.created_at)}
            </p>
          </div>
          <StatusBadge status={feedback.status} />
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
        {/* Revising Banner */}
        {isRevising && (
          <div style={{
            padding: '0.75rem',
            borderRadius: '4px',
            backgroundColor: '#ecfeff',
            border: '1px solid #67e8f9',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}>
            <FontAwesomeIcon icon={faSync} spin style={{ color: CYAN_COLOR }} />
            <div>
              <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#0e7490', margin: 0 }}>
                AI is revising this correction
              </p>
              <p style={{ fontSize: '0.75rem', color: '#0891b2', margin: 0 }}>
                {feedback.admin_notes || 'Processing your feedback...'}
              </p>
            </div>
          </div>
        )}

        {/* Validation Failed Banner */}
        {isValidationFailed && feedback.ai_analysis?.validation && (
          <ValidationFailedBanner
            errors={feedback.ai_analysis.validation.errors || ['Unknown validation error']}
            retryCount={feedback.ai_analysis.validation.retryCount || 0}
            onRetry={onRetryGeneration || (() => {})}
          />
        )}

        {/* User Details */}
        <div style={{ padding: '1rem', borderRadius: '4px', backgroundColor: '#f9fafb', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', margin: '0 0 0.75rem 0' }}>
            👤 User Details
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.875rem' }}>
            <div>
              <p style={{ color: '#6b7280', margin: 0 }}>Email</p>
              <p style={{ color: '#1f2937', margin: 0 }}>{feedback.user_email}</p>
            </div>
            <div>
              <p style={{ color: '#6b7280', margin: 0 }}>Feedback Type</p>
              <p style={{ color: '#1f2937', margin: 0 }}>
                {feedback.feedback_type === 'factual_error' ? 'Factual Error' : 'Behavior Issue'}
              </p>
            </div>
          </div>
        </div>

        {/* Original Conversation */}
        {feedback.conversation_context && (
          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '1rem'
          }}>
            <div style={{
              padding: '0.75rem',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                💬 Original Conversation
              </span>
              <button style={{
                fontSize: '0.75rem',
                color: INFO_COLOR,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}>
                View Full Conversation
              </button>
            </div>
            <div style={{ padding: '1rem', maxHeight: '16rem', overflowY: 'auto' }}>
              {feedback.conversation_context.user_message && (
                <ConversationBubble content={feedback.conversation_context.user_message} isUser />
              )}
              {feedback.conversation_context.ai_response && (
                <ConversationBubble
                  content={feedback.conversation_context.ai_response}
                  isUser={false}
                  source={feedback.conversation_context.source_document}
                />
              )}
              {!feedback.conversation_context.user_message && !feedback.conversation_context.ai_response && (
                <p style={{ fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic', margin: 0 }}>
                  Conversation context not available
                </p>
              )}
            </div>
          </div>
        )}

        {/* RAG Chunks Section */}
        {feedback.conversation_context?.rag_results && feedback.conversation_context.rag_results.length > 0 && (
          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '1rem'
          }}>
            <div style={{
              padding: '0.75rem',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
            }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                🗃️ RAG Chunks Used ({feedback.conversation_context.rag_results.length})
              </span>
            </div>
            <div style={{ padding: '1rem', maxHeight: '20rem', overflowY: 'auto' }}>
              <RagChunkList
                chunks={feedback.conversation_context.rag_results}
                title=""
                showSimilarity={true}
                compact={true}
                maxVisible={3}
                emptyMessage="No RAG chunks were used for this response"
              />
            </div>
          </div>
        )}

        {/* User Feedback */}
        <div style={{
          padding: '1rem',
          borderRadius: '4px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          marginBottom: '1rem'
        }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#b91c1c', margin: '0 0 0.5rem 0' }}>
            ⚠️ User Feedback
          </h3>
          <p style={{ fontSize: '0.875rem', color: '#374151', margin: 0 }}>
            "{feedback.content}"
          </p>
        </div>

        {/* AI Analysis */}
        {feedback.ai_analysis && (
          <div style={{
            padding: '1rem',
            borderRadius: '4px',
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            marginBottom: '1rem'
          }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e40af', margin: '0 0 0.5rem 0' }}>
              🤖 AI Analysis
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#374151', margin: '0 0 0.75rem 0' }}>
              {feedback.ai_analysis.summary}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.875rem' }}>
              <span style={{ color: '#6b7280' }}>Confidence:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{
                  width: '6rem',
                  height: '0.5rem',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '9999px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    width: `${feedback.ai_analysis.confidence}%`,
                    backgroundColor: feedback.ai_analysis.confidence > 80 ? SUCCESS_COLOR :
                                     feedback.ai_analysis.confidence > 60 ? WARNING_COLOR : ERROR_COLOR,
                    borderRadius: '9999px',
                  }} />
                </div>
                <span style={{
                  fontWeight: 500,
                  color: feedback.ai_analysis.confidence > 80 ? SUCCESS_COLOR :
                         feedback.ai_analysis.confidence > 60 ? WARNING_COLOR : ERROR_COLOR,
                }}>
                  {feedback.ai_analysis.confidence}%
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Correction Panel */}
        {feedback.ai_analysis?.correction_type === 'rag' && feedback.ai_analysis.rag_correction && (
          <RagCorrectionPanel
            action={feedback.ai_analysis.rag_correction.action}
            oldChunk={feedback.ai_analysis.rag_correction.old_chunk_id ? {
              id: feedback.ai_analysis.rag_correction.old_chunk_id,
              source: feedback.ai_analysis.rag_correction.old_chunk_source,
              content: feedback.ai_analysis.rag_correction.old_chunk_content,
            } : undefined}
            newChunk={feedback.ai_analysis.rag_correction.new_chunk_content ? {
              id: feedback.ai_analysis.rag_correction.new_chunk_id,
              source: feedback.ai_analysis.rag_correction.new_chunk_source,
              content: feedback.ai_analysis.rag_correction.new_chunk_content,
            } : undefined}
            suggestedDocument={feedback.ai_analysis.rag_correction.suggested_document}
          />
        )}

        {feedback.ai_analysis?.correction_type === 'prompt' && feedback.ai_analysis.prompt_correction && (
          <PromptCorrectionPanel
            action={feedback.ai_analysis.prompt_correction.action}
            currentPrompt={feedback.ai_analysis.prompt_correction.current_prompt_section}
            proposedPrompt={feedback.ai_analysis.prompt_correction.proposed_prompt_section}
          />
        )}
      </div>

      {/* Footer Actions */}
      <div style={{
        padding: '1rem',
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        {/* Left side - Revision or status explanation */}
        <div>
          {actions.canRevise ? (
            <button
              onClick={onRequestRevision}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                fontSize: '0.875rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                border: 'none',
                background: 'transparent',
                color: '#4b5563',
              }}
            >
              💬 Send to AI for Revision
            </button>
          ) : statusExplanation ? (
            <span style={{ fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic' }}>
              {statusExplanation}
            </span>
          ) : null}
        </div>

        {/* Right side - Action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Retry button for failed states */}
          {actions.canRetry && onRetryGeneration && (
            <button
              onClick={onRetryGeneration}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                fontSize: '0.875rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                border: '1px solid #d1d5db',
                background: 'white',
                color: '#374151',
              }}
            >
              <FontAwesomeIcon icon={faRefresh} />
              Retry
            </button>
          )}

          {/* Dismiss button */}
          {actions.canDismiss && (
            <button
              onClick={onDismiss}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                fontSize: '0.875rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                border: '1px solid #d1d5db',
                background: 'white',
                color: '#374151',
              }}
            >
              ✕ Dismiss
            </button>
          )}

          {/* Approve button */}
          {actions.canApprove && (
            <button
              onClick={onApprove}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                fontSize: '0.875rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                border: 'none',
                background: ACCENT_COLOR,
                color: 'white',
              }}
            >
              ✓ Approve Correction
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const RevisionModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (message: string) => void;
}> = ({ isOpen, onClose, onSubmit }) => {
  const [inputMode, setInputMode] = useState<'text' | 'voice'>('text');
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  useEffect(() => {
    let interval: number | undefined;
    if (isRecording) {
      interval = window.setInterval(() => {
        setRecordingTime((t) => t + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = () => {
    if (message.trim()) {
      onSubmit(message);
      setMessage('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '4px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          width: '100%',
          maxWidth: '32rem',
          margin: '0 1rem',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: '1rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <h3 style={{ fontWeight: 600, color: '#1f2937', margin: 0 }}>
            💬 Request AI Revision
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.25rem',
              color: '#9ca3af',
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: '1rem' }}>
          <p style={{ fontSize: '0.875rem', color: '#4b5563', marginBottom: '0.75rem' }}>
            Provide additional context or instructions for the AI to revise its correction proposal.
          </p>

          {/* Input Toggle */}
          <div style={{
            display: 'flex',
            backgroundColor: '#f3f4f6',
            borderRadius: '4px',
            padding: '0.25rem',
            marginBottom: '0.75rem',
          }}>
            <button
              onClick={() => setInputMode('text')}
              style={{
                flex: 1,
                padding: '0.5rem 1rem',
                border: 'none',
                background: inputMode === 'text' ? 'white' : 'transparent',
                boxShadow: inputMode === 'text' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                cursor: 'pointer',
                borderRadius: '4px',
                fontSize: '0.875rem',
              }}
            >
              📝 Type
            </button>
            <button
              onClick={() => setInputMode('voice')}
              style={{
                flex: 1,
                padding: '0.5rem 1rem',
                border: 'none',
                background: inputMode === 'voice' ? 'white' : 'transparent',
                boxShadow: inputMode === 'voice' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                cursor: 'pointer',
                borderRadius: '4px',
                fontSize: '0.875rem',
              }}
            >
              🎤 Record
            </button>
          </div>

          {inputMode === 'text' ? (
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g., 'The replacement chunk is correct, but also check for any other vacation-related content that might be outdated...'"
              style={{
                width: '100%',
                height: '8rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.75rem',
                fontSize: '0.875rem',
                resize: 'none',
                outline: 'none',
              }}
            />
          ) : (
            <div>
              {isRecording && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '4px',
                  marginBottom: '0.75rem',
                }}>
                  <div style={{
                    width: '0.75rem',
                    height: '0.75rem',
                    backgroundColor: '#dc2626',
                    borderRadius: '50%',
                    animation: 'pulse 1s infinite',
                  }} />
                  <span style={{ fontSize: '0.875rem', color: '#b91c1c' }}>
                    Recording... {formatTime(recordingTime)}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                <button
                  onClick={() => setIsRecording(!isRecording)}
                  style={{
                    padding: '1rem 2rem',
                    fontSize: '1rem',
                    borderRadius: '4px',
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: isRecording ? '#dc2626' : ERROR_COLOR,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <FontAwesomeIcon icon={isRecording ? faStop : faMicrophone} />
                  {isRecording ? 'Stop Recording' : 'Start Recording'}
                </button>
              </div>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', textAlign: 'center', marginTop: '0.5rem' }}>
                Audio will be transcribed using Whisper
              </p>
            </div>
          )}
        </div>
        <div style={{
          padding: '1rem',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.5rem',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              border: '1px solid #d1d5db',
              background: 'white',
              color: '#374151',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              border: 'none',
              background: ACCENT_COLOR,
              color: 'white',
            }}
          >
            ✉️ Send for Revision
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================
export const FeedbackReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('all');
  const [feedbackList, setFeedbackList] = useState<FeedbackAdminItem[]>([]);
  const [stats, setStats] = useState<FeedbackAdminStatsResponse>(DEFAULT_STATS);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(null);
  const [isRevisionModalOpen, setIsRevisionModalOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectedFeedback = feedbackList.find((f) => f.id === selectedFeedbackId);

  // Fetch feedback data from API
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch stats and feedback in parallel
      const [statsResult, feedbackResult] = await Promise.all([
        apiService.getFeedbackStats(),
        apiService.listAdminFeedback({ limit: 100 }),
      ]);

      if (statsResult) {
        setStats(statsResult);
      }

      if (feedbackResult) {
        setFeedbackList(feedbackResult.feedback);
        // Select the first item if none selected
        if (feedbackResult.feedback.length > 0 && !selectedFeedbackId) {
          setSelectedFeedbackId(feedbackResult.feedback[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching feedback data:', err);
      setError('Failed to load feedback data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedFeedbackId]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter feedback based on active tab (status-based filtering only)
  const filteredFeedback = feedbackList.filter((f) => {
    if (f.status === 'dismissed') return false; // Hide dismissed by default
    switch (activeTab) {
      case 'pending':
        // Truly pending = waiting for AI generation (no correction yet, or generating)
        return f.status === 'pending' || f.status === 'generating';
      case 'analyzed':
        // Analyzed = AI generated correction, ready for review
        return f.status === 'analyzed' || f.status === 'reviewed';
      case 'revising':
        return f.status === 'revising';
      case 'resolved':
        return f.status === 'resolved' || f.status === 'approved' || f.status === 'applying';
      default:
        return true;
    }
  });

  const showToast = useCallback((message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handleRequestRevision = useCallback(async (message: string) => {
    if (!selectedFeedback) return;

    try {
      const result = await apiService.requestFeedbackRevision(selectedFeedback.id, message);
      if (result) {
        // Update local state
        setFeedbackList((prev) =>
          prev.map((f) =>
            f.id === selectedFeedback.id
              ? { ...f, status: 'revising' as FeedbackAdminStatus, admin_notes: message }
              : f
          )
        );
        showToast('Sent for revision - status updated to Revising');
      } else {
        showToast('Failed to request revision');
      }
    } catch (err) {
      console.error('Error requesting revision:', err);
      showToast('Failed to request revision');
    }
  }, [selectedFeedback, showToast]);

  const handleDismiss = useCallback(async () => {
    if (!selectedFeedback) return;

    try {
      const result = await apiService.dismissFeedback(selectedFeedback.id);
      if (result) {
        // Update local state
        setFeedbackList((prev) =>
          prev.map((f) =>
            f.id === selectedFeedback.id
              ? { ...f, status: 'dismissed' as FeedbackAdminStatus }
              : f
          )
        );
        // Refresh stats
        const newStats = await apiService.getFeedbackStats();
        if (newStats) setStats(newStats);
        showToast('Feedback dismissed');
      } else {
        showToast('Failed to dismiss feedback');
      }
    } catch (err) {
      console.error('Error dismissing feedback:', err);
      showToast('Failed to dismiss feedback');
    }
  }, [selectedFeedback, showToast]);

  const handleApprove = useCallback(async () => {
    if (!selectedFeedback) return;

    const correctionType = selectedFeedback.correction_type === 'rag' ? 'RAG' : 'Prompt';
    const action = selectedFeedback.ai_analysis?.rag_correction?.action ||
                   selectedFeedback.ai_analysis?.prompt_correction?.action || 'update';

    try {
      const result = await apiService.approveFeedbackCorrection(selectedFeedback.id);
      if (result) {
        // Update local state
        setFeedbackList((prev) =>
          prev.map((f) =>
            f.id === selectedFeedback.id
              ? { ...f, status: 'resolved' as FeedbackAdminStatus }
              : f
          )
        );
        // Refresh stats
        const newStats = await apiService.getFeedbackStats();
        if (newStats) setStats(newStats);
        showToast(`${correctionType} ${action} approved and applied`);
      } else {
        showToast('Failed to approve correction');
      }
    } catch (err) {
      console.error('Error approving correction:', err);
      showToast('Failed to approve correction');
    }
  }, [selectedFeedback, showToast]);

  const handleRetryGeneration = useCallback(async () => {
    if (!selectedFeedback) return;

    try {
      // Update status to show we're regenerating
      setFeedbackList((prev) =>
        prev.map((f) =>
          f.id === selectedFeedback.id
            ? { ...f, status: 'analyzing' as FeedbackAdminStatus }
            : f
        )
      );

      // Call the generate-correction endpoint (will retry generation)
      const result = await apiService.generateFeedbackCorrection(selectedFeedback.id);
      if (result) {
        // Update with new status from server
        setFeedbackList((prev) =>
          prev.map((f) =>
            f.id === selectedFeedback.id
              ? { ...f, status: result.status as FeedbackAdminStatus, ai_analysis: result.ai_analysis }
              : f
          )
        );
        if (result.ai_analysis?.validation?.passed) {
          showToast('Correction regenerated successfully');
        } else {
          showToast('Regeneration failed validation again');
        }
        // Refresh data to get updated state
        fetchData();
      } else {
        showToast('Failed to regenerate correction');
        // Revert status
        setFeedbackList((prev) =>
          prev.map((f) =>
            f.id === selectedFeedback.id
              ? { ...f, status: 'validation_failed' as FeedbackAdminStatus }
              : f
          )
        );
      }
    } catch (err) {
      console.error('Error regenerating correction:', err);
      showToast('Failed to regenerate correction');
      setFeedbackList((prev) =>
        prev.map((f) =>
          f.id === selectedFeedback.id
            ? { ...f, status: 'validation_failed' as FeedbackAdminStatus }
            : f
        )
      );
    }
  }, [selectedFeedback, showToast, fetchData]);

  const handleBackToAdmin = () => {
    navigate('/lupa-admin');
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f3f4f6',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: DARK_COLOR,
        color: 'white',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      }}>
        <div style={{
          maxWidth: '80rem',
          margin: '0 auto',
          padding: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              onClick={handleBackToAdmin}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                opacity: 0.8,
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back
            </button>
            <div style={{
              width: '2.5rem',
              height: '2.5rem',
              backgroundColor: ACCENT_COLOR,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.25rem',
            }}>
              💬
            </div>
            <div>
              <h1 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Feedback Review</h1>
              <p style={{ fontSize: '0.875rem', color: '#9ca3af', margin: 0 }}>Lupa Administration</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              onClick={fetchData}
              disabled={isLoading}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              <FontAwesomeIcon icon={faRefresh} spin={isLoading} />
              Refresh
            </button>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: 'rgba(250,204,21,0.2)',
              color: '#facc15',
              padding: '0.25rem 0.75rem',
              borderRadius: '9999px',
              fontSize: '0.875rem',
            }}>
              <span style={{
                width: '0.5rem',
                height: '0.5rem',
                borderRadius: '50%',
                backgroundColor: '#facc15',
                animation: 'pulse 2s infinite',
              }} />
              <span>{stats.pending} Pending</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ maxWidth: '80rem', margin: '0 auto', padding: '1.5rem 1rem' }}>
        {/* Loading State */}
        {isLoading && feedbackList.length === 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '4rem',
            color: '#6b7280',
          }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
            Loading feedback data...
          </div>
        )}

        {/* Error State */}
        {error && (
          <div style={{
            padding: '1rem',
            borderRadius: '4px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#b91c1c',
            marginBottom: '1.5rem',
          }}>
            {error}
          </div>
        )}

        {/* Stats Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: '0.75rem',
          marginBottom: '1.5rem',
        }}>
          <StatCard label="Total" value={stats.total} icon="📥" bgColor="#dbeafe" textColor="#1f2937" />
          <StatCard label="Pending" value={stats.pending} icon="⏰" bgColor="#fef3c7" textColor="#ca8a04" />
          <StatCard label="Ready for Review" value={stats.analyzed || 0} icon="👁️" bgColor="#dbeafe" textColor={INFO_COLOR} />
          <StatCard label="Revising" value={stats.revising} icon="🔄" bgColor="#cffafe" textColor={CYAN_COLOR} />
          <StatCard label="Resolved" value={stats.resolved} icon="✅" bgColor="#dcfce7" textColor={SUCCESS_COLOR} />
          <StatCard label="RAG / Prompt" value={stats.by_correction_type.rag + stats.by_correction_type.prompt} icon="🏷️" bgColor="#f3e8ff" textColor="#7c3aed" />
        </div>

        {/* Tabs - status-based filtering only */}
        <div style={{
          backgroundColor: 'white',
          borderTopLeftRadius: '0.75rem',
          borderTopRightRadius: '0.75rem',
          border: '1px solid #e5e7eb',
          borderBottom: 'none',
        }}>
          <div style={{ display: 'flex', gap: '1rem', padding: '0 1.5rem' }}>
            {[
              { id: 'all' as TabId, label: `All (${stats.total})` },
              { id: 'pending' as TabId, label: `Pending (${stats.pending})` },
              { id: 'analyzed' as TabId, label: `Ready for Review (${stats.analyzed || 0})` },
              { id: 'revising' as TabId, label: `Revising (${stats.revising})` },
              { id: 'resolved' as TabId, label: `Resolved (${stats.resolved})` },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '1rem 0',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: activeTab === tab.id ? ACCENT_COLOR : '#6b7280',
                  borderBottom: `3px solid ${activeTab === tab.id ? ACCENT_COLOR : 'transparent'}`,
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderTop: 'none',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Two-Column Layout */}
        <div style={{
          display: 'flex',
          backgroundColor: 'white',
          borderBottomLeftRadius: '0.75rem',
          borderBottomRightRadius: '0.75rem',
          border: '1px solid #e5e7eb',
          minHeight: '600px',
        }}>
          {/* Left: Feedback List */}
          <div style={{
            width: '40%',
            borderRight: '1px solid #e5e7eb',
            overflowY: 'auto',
          }}>
            {filteredFeedback.length === 0 ? (
              <div style={{
                padding: '3rem',
                textAlign: 'center',
                color: '#6b7280',
              }}>
                <FontAwesomeIcon icon={faInbox} style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
                <p>No feedback items in this category</p>
              </div>
            ) : (
              filteredFeedback.map((feedback) => (
                <FeedbackListItem
                  key={feedback.id}
                  feedback={feedback}
                  isSelected={feedback.id === selectedFeedbackId}
                  onClick={() => setSelectedFeedbackId(feedback.id)}
                />
              ))
            )}
          </div>

          {/* Right: Detail Panel */}
          <div style={{ width: '60%', display: 'flex', flexDirection: 'column' }}>
            {selectedFeedback ? (
              <FeedbackDetailPanel
                feedback={selectedFeedback}
                onRequestRevision={() => setIsRevisionModalOpen(true)}
                onDismiss={handleDismiss}
                onApprove={handleApprove}
                onRetryGeneration={handleRetryGeneration}
              />
            ) : (
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <FontAwesomeIcon icon={faComment} style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
                  <p>Select a feedback item to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Revision Modal */}
      <RevisionModal
        isOpen={isRevisionModalOpen}
        onClose={() => setIsRevisionModalOpen(false)}
        onSubmit={handleRequestRevision}
      />

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '1rem',
          right: '1rem',
          backgroundColor: SUCCESS_COLOR,
          color: 'white',
          padding: '0.75rem 1.5rem',
          borderRadius: '4px',
          boxShadow: '0 10px 15px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          zIndex: 100,
        }}>
          <span>✓</span>
          <span>{toast}</span>
        </div>
      )}

      {/* Pulse animation keyframes */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default FeedbackReviewPage;
